<template>
	<span>
		<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Modernized folder action bar: sort left, primary Add button right -->
		<div class="section-action-bar mb-2">
			<div class="action-bar-sort">
				<span class="action-bar-label">Sort</span>
				<b-button
					size="sm"
					variant="outline-secondary"
					class="action-bar-sort-btn"
					@click="sortFoldersAlpha"
				>
					<b-icon :icon="alphaSortDirection > 0 ? 'sort-alpha-down' : 'sort-alpha-up'" />
					Name
				</b-button>
				<b-button
					size="sm"
					variant="outline-secondary"
					class="action-bar-sort-btn"
					@click="sortFoldersCustom"
				>
					<b-icon :icon="numericSortDirection > 0 ? 'sort-numeric-down' : 'sort-numeric-up'" />
					Order
				</b-button>
			</div>
			<b-button
				size="sm"
				variant="primary"
				class="action-bar-add-btn"
				@click="addFolder()"
			>
				<b-icon-plus />
				Add Folder
			</b-button>
		</div>

		<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Fixed column widths: 1+4+2+1+1+1+2=12; Order=2 matches Contexts table -->
		<b-row class="mb-2" cols="12">
			<b-col cols="1"/>
			<b-col cols="4"><span class="col-header">Name</span></b-col>
			<b-col cols="2"><span class="col-header">Group</span></b-col>
			<b-col cols="1" class="text-center"><span class="col-header">Color</span></b-col>
			<b-col cols="1" class="text-center"><span class="col-header">Private</span></b-col>
			<b-col cols="1" class="text-center"><span class="col-header">Active</span></b-col>
			<b-col cols="2"><span class="col-header">Order</span></b-col>
		</b-row>

		<b-row
			v-for="(folder, index) in localFolders"
			:key="index"
			:class="{ 'bg-danger': folder.deleteOp, 'p-2': 1 == 1, 'bg-success': folder.addOp }"
			cols="12"
		>
			<b-col cols="1">
				
				<b-button 
					v-if="typeof folder.deleteOp == 'undefined' || folder.deleteOp === null || !folder.deleteOp"
					@click="toggleFolderField(index, 'deleteOp')" 
					:id="'button_delete_' + index"
					size="sm"
					variant="white">
					
					<b-icon-trash variant="danger"/>
				
				</b-button>

				<b-tooltip 
					v-if="typeof folder.deleteOp == 'undefined' || folder.deleteOp === null || !folder.deleteOp"
					:target="'button_delete_' + index"
					triggers="hover"
					variant="danger">
				
					Click to delete this folder.

				</b-tooltip>

				<b-button 
					v-if="folder.deleteOp"
					@click="toggleFolderField(index, 'deleteOp')" 
					:id="'button_undo_' + index"
					size="sm"
					variant="white">
					
					<b-icon-arrow-counterclockwise variant="warning"/>
				
				</b-button>

				<b-tooltip 
					v-if="folder.deleteOp"
					:target="'button_undo_' + index"
					triggers="hover"
					variant="warning">
				
					Click to undo folder deletion.

				</b-tooltip>

			</b-col>
			<b-col cols="4">
				<b-form-input v-model.trim="folder.name" />
				<b-form-valid-feedback :state="folderValid[index].name">
					Looks good!
				</b-form-valid-feedback>

				<b-form-invalid-feedback :state="folderValid[index].name">
					A name for the folder must be provided.
				</b-form-invalid-feedback>
			</b-col>

			<b-col cols="2">
				<b-form-input v-model.trim="folder.group" />
			</b-col>

			<!-- 20260418 -- Copilot (Claude Sonnet 4.6) -- Color swatch picker; free for all users -->
			<b-col cols="1" class="d-flex justify-content-center align-items-center">
				<color-swatch-picker
					:value="folder.color || null"
					@input="folder.color = $event"
				/>
			</b-col>

			<b-col cols="1" class="d-flex justify-content-center">
				<!--
					NOTE: Using explicit :model-value binding instead of v-model due to Vue 3 reactivity edge case
					WHEN: Dec 2024 - Vue 3 Migration
					WHY: Standard v-model doesn't properly bind to dynamically loaded reactive array items in this context
					WHAT: Explicitly bind :model-value and @update:modelValue for two-way data binding
				-->
				<b-form-checkbox-compat
					:model-value="folder.private"
					@update:modelValue="folder.private = $event"
					name="private"
					value="Y"
					unchecked-value="N"
				/>
			</b-col>

			<b-col cols="1" class="d-flex justify-content-center">
				<b-form-checkbox-compat
					:model-value="folder.active"
					@update:modelValue="folder.active = $event"
					name="active"
					value="Y"
					unchecked-value="N"
				/>
			</b-col>

			<b-col cols="2">
				<b-form-input v-model.trim="folder.customOrder" type="number" />

				<b-form-valid-feedback>
					Looks good!
				</b-form-valid-feedback>

				<b-form-invalid-feedback>
					A title (name) for the task must be provided.
				</b-form-invalid-feedback>
			</b-col>
		</b-row>

		<b-row class="mt-4">
			<b-col cols="12" style="text-align:right;">
				<b-button ref="folderFormResetButton" class="mr-2" variant="warning" @click="onCancelOfFolderForm()"
					>Cancel</b-button
				>

				<b-button
					ref="folderFormSubmitButton"
					class="mr-2"
					variant="success"
					@click="onSubmitOfFolderForm()"
					>Save</b-button
				>
			</b-col>
		</b-row>
	</span>
</template>

<script>

import _ from 'lodash';
import Logger from '@/utils/Logger'
// 20260416 -- Copilot (Claude Sonnet 4.6) -- Added genericOps for isPremiumUser() to gate color picker
import genericOps from '@/mixins/genericOps'

const log = Logger.create('FolderManagement', { showCaller: true, showLineNumbers: true })

export default {
	// 20260416 -- Copilot (Claude Sonnet 4.6) -- Added genericOps mixin for isPremiumUser()
	mixins: [genericOps],
	data() {
		return {
			localFolders: [],
			folderValid: [],
			alphaSortDirection: 1,
			numericSortDirection: 1,
		}
	},
	async created() {
		await this.resetFolderData()
	},
	computed: {
		
	},
	methods: {
		addFolder() {
			// 20260203 -- Copilot (Claude Haiku 4.5) -- Set default private and active to true for new folders
			this.folderValid.unshift({ name: null })
			this.localFolders.unshift({
				addOp: true,
				owner: this.$store.getters.getUser._id,
				active: 'Y',
				private: 'Y',
			})
		},

		onCancelOfFolderForm() {
			this.resetFolderData();

			this.$emit('cancelled:FolderManagement', true, 'Profile Update', 'Action cancelled at user\'s request.  No data was changed.', 'warning')

		},
		async onSubmitOfFolderForm() {
			this.$refs.folderFormSubmitButton.disabled = true
			this.$refs.folderFormResetButton.disabled = true

			let valid = true

			this.folderValid = []

			this.folderValid = this.localFolders.map((x) => {
				return { name: null }
			})

			for (let i = 0; i < this.localFolders.length; i++) {
				if (typeof this.localFolders[i].name == 'undefined' || this.localFolders[i].name === null) {
					
					let temp = {}
					temp.name = false
					this.$set(this.folderValid, i, temp)

					valid = false
				}

				if (
					!(typeof this.localFolders[i].customOrder == 'undefined') &&
					!(this.localFolders[i].customOrder === null)
				) {
					this.localFolders[i].customOrder = Number(this.localFolders[i].customOrder)
				}
			}

			if (valid) {
				log.info('Dispatching "updateFolders" to $store', this.localFolders)
				await this.$store.dispatch('updateFolders', this.localFolders)
				this.$emit('submitted:FolderManagement', true, 'Profile Update', 'Folders updated.', 'success');
				this.resetFolderData()
			} else {
				this.$emit('submitted:FolderManagement', true, 'Profile Update', 'Unable to save folder changes.  Review folder list for details.', 'danger');
				this.$refs.folderFormSubmitButton.disabled = false
				this.$refs.folderFormResetButton.disabled = false
			}
		},
		async prepareFolderData() {
			let that = this

			try {
				await FolderService.getFolders(
					this.$store.getters.getUser._id,
					false,
					this.$store.getters.getUserToken
				)
					.then(function(response) {
						response.sort(function(a, b) {
							if (a.customOrder > b.customOrder) {
								return 1
							} else if (a.customOrder < b.customOrder) {
								return -1
							} else {
								return 0
							}
						})

						that.localFolders = response

						that.folderValid = that.localFolders.map((x) => {
							return { name: null }
						})
					})
					.catch((error) => {
						//////console.log('Error loading folders: ' + error)
					})
			} catch (e) {
				//////console.log('Error loading folder date: ' + e)
			}
		},
		async resetFolderData() {
			
			this.localFolders = []
			this.localFolders = _.concat(this.localFolders, await this.$store.dispatch('getFoldersForProfile'))

			// Create array for validation.
			this.folderValid = this.localFolders.map((x) => {
				return { name: null }
			})

			// Enable buttons
			try {
				this.$refs.folderFormSubmitButton.disabled = false
				this.$refs.folderFormResetButton.disabled = false
			} catch (e) {
				// Intentionally left blank
			}

		},
		sortFoldersAlpha() {
			//////console.log('Profile.sortFoldersAlpha(' + direction + ')')

			this.alphaSortDirection = this.alphaSortDirection * -1;

			let that = this

			this.localFolders.sort(function(a, b) {
				if (a.name > b.name) {
					return 1 * that.alphaSortDirection
				} else if (a.name < b.name) {
					return -1 * that.alphaSortDirection
				} else {
					if (a.customOrder > b.customOrder) {
						return 1 * that.alphaSortDirection
					} else if (a.customOrder < b.customOrder) {
						return -1 * that.alphaSortDirection
					} else {
						return 0
					}
				}
			})

			for (let i = 0; i < this.localFolders.length; i++) {
				//this.localFolders[i].customOrder = (i + 1) * 5
				let temp = this.localFolders[i];
				temp.customOrder = (i + 1) * 5;
				this.$set(this.localFolders, i, temp)
			}

			//this.sortFolders()
		},
		sortFoldersCustom(direction) {
			//////console.log('Profile.sortFoldersCustom(' + direction + ')')

			this.numericSortDirection = this.numericSortDirection * -1

			let that = this

			this.localFolders.sort(function(a, b) {
				if (a.customOrder > b.customOrder) {
					return 1 * that.numericSortDirection
				} else if (a.customOrder < b.customOrder) {
					return -1 * that.numericSortDirection
				} else {
					if (a.name > b.name) {
						return 1 * that.numericSortDirection
					} else if (a.name < b.name) {
						return -1 * that.numericSortDirection
					} else {
						return 0
					}
				}
			})

			for (let i = 0; i < this.localFolders.length; i++) {
				let temp = this.localFolders[i];
				temp.customOrder = (i + 1) * 5;
				this.$set(this.localFolders, i, temp)
			}

			//this.sortFolders()
		},
		toggleFolderField(index, field) {
			if (typeof this.localFolders[index]._id == 'undefined' || this.localFolders[index]._id === null) {
				this.localFolders.splice(index, 1)
				this.folderValid.splice(index, 1)
			} else {
				let val = this.localFolders[index][field]

				if (typeof val == 'undefined' || val === null) {
					val = true
				} else if (val) {
					val = false
				} else {
					val = true
				}

				this.$set(this.localFolders[index], field, val)
			}
		},
	},
}
</script>

<style scoped>
/* 20260416 -- Copilot (Claude Sonnet 4.6) -- Matches .detail-label in Profile.vue for consistent column header typography */
.col-header {
  font-size: 0.625rem;
  font-weight: 500;
  color: var(--text-secondary, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* 20260416 -- Copilot (Claude Sonnet 4.6) -- Action bar for folder toolbar section */
.section-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.375rem 0;
}

.action-bar-sort {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.action-bar-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-right: 0.125rem;
}

.action-bar-sort-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
}

.action-bar-add-btn {
  display: inline-flex !important;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
}
</style>
